import { useState, FormEvent } from 'react';
import { motion } from 'motion/react';

interface FreeTextInputProps {
  placeholder?: string;
  onSubmit: (text: string) => void;
  defaultValue?: string;
}

export function FreeTextInput({ placeholder = 'Type something...', onSubmit, defaultValue = '' }: FreeTextInputProps) {
  const [value, setValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim()) {
      onSubmit(value.trim());
      setValue('');
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="relative"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <label htmlFor="free-text-input" className="sr-only">
        Free text input
      </label>
      <div className="relative">
        <textarea
          id="free-text-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`input resize-none min-h-[80px] ${isFocused ? 'border-accent-primary ring-2 ring-accent-primary/20' : ''}`}
          rows={3}
        />
        {value && (
          <motion.button
            type="button"
            onClick={() => setValue('')}
            className="absolute right-3 top-3 text-text-muted hover:text-text-primary transition-colors"
            whileTap={{ scale: 0.9 }}
            aria-label="Clear input"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </motion.button>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-text-muted">
          {value.length} characters
        </span>
        <motion.button
          type="submit"
          disabled={!value.trim()}
          className="btn-primary opacity-0 group-hover:opacity-100 transition-opacity"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Save note
          <ArrowRightIcon className="w-4 h-4 ml-1.5" />
        </motion.button>
      </div>
    </motion.form>
  );
}

function ArrowRightIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  );
}